import React, { useCallback, useEffect } from 'react';

import { Flex } from '@chakra-ui/react';
import { HeaderMarginContainer } from 'Deer/components/HeaderComponents/HeaderMarginContainer';
import { L3_PATHS } from 'Deer/constants/routes';
import { useDeerViewId } from 'Deer/hooks/useDeerViewId';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { setSelectedReviewCycle } from 'reducers/reviewManageSidepanelSlice';
import { setSelectedCalibrationView, useTalentPlanningSidepanelData } from 'reducers/talentPlanningSidepanelSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import useDeerTeamBasedReviewRoute from 'screens/performanceReview/hooks/useDeerTeamBasedReviewRoute';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { getEncodedCohortQueryParam } from 'constants/helper';
import { sendSelectedReviewCycleEvent } from 'constants/mixpanelHelpers/reviewsEvents';
import { routes } from 'constants/routes';

import { TextHeader2 } from 'uiToolkitV1/Typography';

import { ReviewViewsSelection } from './ReviewViewsSelection';

function TabbedNavigationTitle({
  cohortName,
  selectedReviewCycle,
  queryParamReviewCycleId,
  queryParamCalibrationViewId,
  reviewCycleAndViewOptions,
  onReviewCycleChange,
}) {
  const dispatch = useDispatch();
  const cohortDetails = useSelectedView();
  const teamBasedReviewRoute = useDeerTeamBasedReviewRoute();
  const history = useHistory();
  const isTalentPlanningPage = history.location.pathname.includes(routes.performanceReview.talentPlanning);
  const { viewId: viewIdFromQueryParams } = useDeerViewId();
  const viewIdToAppend = viewIdFromQueryParams ? `viewId=${viewIdFromQueryParams}&` : '';
  const { selectedCalibrationView } = useTalentPlanningSidepanelData();

  const isReviewViewReviewManagePage = useRouteMatch({
    path: L3_PATHS.team.view.review.manage,
    exact: true,
  });

  const isReviewViewCalibrationPage = useRouteMatch({
    path: L3_PATHS.team.view.review.calibrationView,
    exact: true,
  });

  const handleReviewCycleChange = useCallback(
    (reviewCycle) => {
      if (typeof onReviewCycleChange === 'function') {
        onReviewCycleChange(reviewCycle);
      }

      sendSelectedReviewCycleEvent({
        eventProperties: {
          cycleName: reviewCycle.displayName,
          cycleId: reviewCycle.uuid,
        },
      });

      dispatch(setSelectedReviewCycle(reviewCycle));

      if (reviewCycle && cohortDetails) {
        const cohortQueryString = getEncodedCohortQueryParam(cohortDetails, history.location, false);
        history.push({
          pathname: teamBasedReviewRoute,
          search: `${viewIdToAppend}${cohortQueryString}&reviewCycleId=${reviewCycle.uuid}`,
        });
      }
    },
    [onReviewCycleChange, dispatch, cohortDetails, history, teamBasedReviewRoute, viewIdToAppend]
  );

  const handleCalibrationViewChange = useCallback(
    (calibrationView) => {
      dispatch(setSelectedCalibrationView(calibrationView));

      const cohortQueryString = getEncodedCohortQueryParam(cohortDetails, history.location, true);

      const urlSearchParams = new URLSearchParams(cohortQueryString);
      urlSearchParams.set('calibrationViewId', calibrationView.uuid);
      if (viewIdToAppend) {
        urlSearchParams.set('viewId', viewIdFromQueryParams);
      }
      const search = urlSearchParams.toString();

      const getNewNavRedirectPageLink = () => {
        if (isReviewViewReviewManagePage || isReviewViewCalibrationPage) {
          return L3_PATHS.team.view.review.calibrationView;
        }
        return L3_PATHS.company.review.calibrationView;
      };

      history.push({
        pathname: checkNewNavEnabled() ? getNewNavRedirectPageLink() : routes.performanceReview.talentPlanning,
        search,
      });
    },
    [
      dispatch,
      cohortDetails,
      history,
      viewIdToAppend,
      viewIdFromQueryParams,
      isReviewViewReviewManagePage,
      isReviewViewCalibrationPage,
    ]
  );

  useEffect(() => {
    if (reviewCycleAndViewOptions?.reviewCycles?.length === 0) {
      return;
    }

    const hasQueryParamReviewCycleId = queryParamReviewCycleId && queryParamReviewCycleId !== 'null';
    const hasQueryParamCalibrationViewId = queryParamCalibrationViewId && queryParamCalibrationViewId !== 'null';

    const hasReduxStoreReviewCycle = selectedReviewCycle;
    const reduxStoreReviewCycleId = selectedReviewCycle?.uuid;

    const hasReduxStoreCalibrationView = selectedCalibrationView;
    const reduxStoreCalibrationViewId = selectedCalibrationView?.uuid;

    if (
      (hasQueryParamReviewCycleId && queryParamReviewCycleId === reduxStoreReviewCycleId) ||
      (hasQueryParamCalibrationViewId && queryParamCalibrationViewId === reduxStoreCalibrationViewId)
    ) {
      return;
    }

    const isValidReviewCycleId = (reviewCycleId) =>
      reviewCycleAndViewOptions.reviewCycles.find((rc) => rc.uuid === reviewCycleId);

    const isValidCalibrationViewId = (calibrationViewId) =>
      reviewCycleAndViewOptions.calibrationViews.find((av) => av.uuid === calibrationViewId);

    // query param takes precedence over redux store
    if (!isTalentPlanningPage) {
      if (hasQueryParamReviewCycleId && queryParamReviewCycleId !== reduxStoreReviewCycleId) {
        if (isValidReviewCycleId(queryParamReviewCycleId)) {
          handleReviewCycleChange(
            reviewCycleAndViewOptions.reviewCycles.find((rc) => rc.uuid === queryParamReviewCycleId)
          );
        } else {
          handleReviewCycleChange(reviewCycleAndViewOptions.reviewCycles[0]);
        }
      } else if (!hasQueryParamReviewCycleId && hasReduxStoreReviewCycle) {
        if (isValidReviewCycleId(reduxStoreReviewCycleId)) {
          handleReviewCycleChange(
            reviewCycleAndViewOptions.reviewCycles.find((rc) => rc.uuid === reduxStoreReviewCycleId)
          );
        } else {
          handleReviewCycleChange(reviewCycleAndViewOptions.reviewCycles[0]);
        }
      } else {
        handleReviewCycleChange(reviewCycleAndViewOptions.reviewCycles[0]);
      }
    }

    if (isTalentPlanningPage) {
      if (hasQueryParamCalibrationViewId && queryParamCalibrationViewId !== reduxStoreCalibrationViewId) {
        if (isValidCalibrationViewId(queryParamCalibrationViewId)) {
          handleCalibrationViewChange(
            reviewCycleAndViewOptions.calibrationViews.find((av) => av.uuid === queryParamCalibrationViewId)
          );
        } else {
          handleCalibrationViewChange(reviewCycleAndViewOptions.calibrationViews[0]);
        }
      } else if (!hasQueryParamCalibrationViewId && hasReduxStoreCalibrationView) {
        if (isValidCalibrationViewId(reduxStoreCalibrationViewId)) {
          handleCalibrationViewChange(
            reviewCycleAndViewOptions.calibrationViews.find((av) => av.uuid === reduxStoreCalibrationViewId)
          );
        } else {
          handleCalibrationViewChange(reviewCycleAndViewOptions.calibrationViews[0]);
        }
      } else {
        handleCalibrationViewChange(reviewCycleAndViewOptions.calibrationViews[0]);
      }
    }
  }, [
    reviewCycleAndViewOptions,
    selectedCalibrationView,
    queryParamCalibrationViewId,
    queryParamReviewCycleId,
    selectedReviewCycle,
    handleCalibrationViewChange,
    handleReviewCycleChange,
    isTalentPlanningPage,
  ]);

  let headerText = '';

  if (cohortName === 'My Team') {
    headerText = checkNewNavEnabled() ? 'My direct team’s reviews for' : 'My Team’s Reviews for';
  } else if (cohortName === 'My Company') {
    headerText = checkNewNavEnabled() ? "My company's reviews for" : "My Company's Reviews for";
  } else if (cohortName === 'My Organization Tree') {
    headerText = "My Organization Tree's Reviews for";
  } else if (cohortName === 'My Organization') {
    headerText = 'My skip team’s reviews for';
  } else {
    headerText = cohortName;
  }

  return (
    <HeaderMarginContainer>
      <Flex alignItems="center" position="relative" zIndex="10">
        <TextHeader2 mr="8px" py={2} bold>
          {headerText}
        </TextHeader2>

        <ReviewViewsSelection
          reviewCycleAndViewOptions={reviewCycleAndViewOptions}
          onReviewCycleChange={handleReviewCycleChange}
          onCalibrationViewChange={handleCalibrationViewChange}
        />
      </Flex>
    </HeaderMarginContainer>
  );
}

export default TabbedNavigationTitle;
